import React from 'react';

/**
 * Shared building blocks for the Settings detail sections (Profile, Security, Accreditation,
 * Docs). Keeps the panel surface, section headers, and key/value rows consistent in one place.
 */

/** Subtle-fill bordered panel used for rows/cards across every section. */
export const SETTINGS_PANEL = 'rounded-lg border border-border bg-secondary/50';

/**
 * Primary CTA look for the settings/onboarding surfaces: a neutral black button (never the gold
 * `--primary` fill), with matching hover + focus ring. Pass to a `<Button>`'s className to override
 * its default gold variant. Keeps every primary action consistent with the banner's black CTA.
 */
export const PRIMARY_BTN =
  'bg-foreground text-background hover:bg-foreground/90 focus-visible:border-foreground focus-visible:ring-foreground/50';

/** Uppercase eyebrow section header with an optional right-aligned action (e.g. Edit / Save). */
export function SectionHeader({ title, action }: { title: string; action?: React.ReactNode }) {
  return (
    <div className="mb-3 flex items-center justify-between gap-3">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{title}</h3>
      {action}
    </div>
  );
}

/** Bordered, subtle-fill definition list — dividers between rows. Wrap <InfoRow>s. */
export function InfoList({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return <dl className={`${SETTINGS_PANEL} divide-y divide-border ${className}`}>{children}</dl>;
}

/** A label → value row (label left, value right). Use inside <InfoList>. */
export function InfoRow({
  label,
  value,
  labelExtra,
}: {
  label: string;
  value: React.ReactNode;
  labelExtra?: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1 px-4 py-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
      <dt className="flex items-center gap-2 text-sm text-muted-foreground sm:flex-shrink-0">
        {label}
        {labelExtra}
      </dt>
      <dd className="min-w-0 break-words text-sm font-medium text-foreground sm:text-right">{value}</dd>
    </div>
  );
}
